<template>
  <main
    class="app"
    ref="appBox"
  >
    <!-- Header -->
    <header class="header">
      <div class="brand">
        <span class="brand-mark">AI</span>
        <span>
          <strong>Conversify</strong>
          <small>AI travel assistant</small>
        </span>
      </div>
      <span class="maker">Made by ITAINCTW</span>
    </header>

    <div class="content">
      <!-- Hero 區塊 -->
      <section
        class="hero"
        aria-label="AI 旅遊助手"
      >
        <img
          src="@/assets/conversify-assistant-once.gif"
          alt="AI 旅遊助手動畫"
        />
        <div class="hero-caption">
          <span>✦</span>
          <strong>探索世界，從一次對話開始</strong>
          <small>景點、行程、美食與交通，都可以問我</small>
        </div>
      </section>

      <!-- 聊天主要內容 -->
      <section class="chat-panel">
        <div
          class="chat"
          ref="chatBox"
        >
          <!-- 預設歡迎訊息 -->
          <div class="message-row ai">
            <span class="avatar">
              <img
                src="@/assets/conversify-assistant-avatar.png"
                alt="AI Avatar"
              />
            </span>
            <div class="bubble">
              哈囉，讓我擔任您的旅遊小助手，一起暢遊世界吧！
            </div>
          </div>

          <!-- 目的地輸入卡片 -->
          <section
            v-if="!isStarted"
            class="destination-card"
          >
            <label for="destination"
              >請輸入即將前往的國家與區域 <em>*</em></label
            >
            <input
              id="destination"
              v-model.trim="destination"
              placeholder="例如：日本東京"
              autocomplete="off"
              @keydown.enter="handleStart"
            />
            <p
              v-if="errorMessage"
              class="error"
            >
              {{ errorMessage }}
            </p>
            <button
              class="start-button"
              @click="handleStart"
            >
              開始對話 →
            </button>
          </section>

          <!-- 對話記錄區塊 -->
          <div v-else>
            <!-- 目的地標籤 -->
            <div class="destination-tag">
              📍 目的地：<span>{{ destination }}</span>
              <button @click="handleChangeDestination">變更</button>
            </div>

            <!-- 建議問題快捷按鈕 (Chips) -->
            <div
              class="chips"
              aria-label="建議問題"
            >
              <button
                v-for="(suggestion, index) in suggestions"
                :key="index"
                @click="sendMessage(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>

            <!-- 對話訊息列表 -->
            <div aria-live="polite">
              <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="['message-row', msg.role]"
              >
                <span
                  v-if="msg.role === 'assistant'"
                  class="avatar"
                >
                  <img
                    src="@/assets/conversify-assistant-avatar.png"
                    alt="AI Avatar"
                  />
                </span>
                <div class="bubble">
                  <!-- 如果訊息有 Base64 圖片，顯示縮圖 -->
                  <div
                    v-if="msg.imageUrl"
                    class="image-preview"
                  >
                    <img
                      :src="msg.imageUrl"
                      alt="上傳的照片"
                    />
                  </div>
                  <div>{{ msg.content }}</div>
                </div>
              </div>

              <!-- Typing 動畫 -->
              <div
                v-if="isTyping"
                class="message-row ai"
                role="status"
              >
                <span class="avatar">
                  <img
                    src="@/assets/conversify-assistant-avatar.png"
                    alt="AI Avatar"
                  />
                </span>
                <div class="bubble">
                  <span class="typing"><i></i><i></i><i></i></span>
                  旅遊小助手正在為您查找，請稍等…
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部輸入框與操作按鈕 -->
        <div
          v-if="isStarted"
          class="composer-wrap"
        >
          <!-- 待發送照片預覽區 & 狀態訊息 -->
          <div class="status-bar">
            <div
              v-if="photo"
              class="photo-preview-box"
            >
              <img
                :src="photo"
                alt="待發送照片"
              />
              <button
                class="remove-photo-btn"
                title="移除照片"
                @click="removePhoto"
              >
                ✕
              </button>
            </div>
            <span
              v-if="statusText"
              class="status-text"
              >{{ statusText }}</span
            >
          </div>

          <div class="composer">
            <!-- 隱藏的拍照 Input -->
            <input
              ref="cameraInput"
              type="file"
              accept="image/*"
              capture="environment"
              aria-label="使用手機後鏡頭拍照"
              hidden
              @change="handleCameraChange"
            />

            <!-- 相機按鈕 -->
            <button
              class="icon-button camera"
              aria-label="開啟後鏡頭拍照"
              title="拍照"
              @click="triggerCamera"
            >
              <svg viewBox="0 0 24 24">
                <path d="M4 8h3l1.5-2h7L17 8h3v10H4z" />
                <circle
                  cx="12"
                  cy="13"
                  r="3"
                />
              </svg>
            </button>

            <!-- 文字輸入框 -->
            <textarea
              v-model="inputText"
              rows="1"
              placeholder="詢問行程、景點、美食或交通…"
              aria-label="聊天訊息"
              @keydown.enter="handleEnterKey"
            ></textarea>

            <!-- 麥克風按鈕 -->
            <button
              class="icon-button mic"
              aria-label="啟動語音輸入"
              @click="handleSpeechRecognition"
            >
              <svg viewBox="0 0 24 24">
                <rect
                  x="9"
                  y="3"
                  width="6"
                  height="11"
                  rx="3"
                />
                <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" />
              </svg>
            </button>

            <!-- 送出按鈕 -->
            <button
              class="icon-button send"
              aria-label="送出訊息"
              @click="sendMessage()"
            >
              <svg viewBox="0 0 24 24">
                <path d="m3 11 18-8-8 18-2-8zM11 13 21 3" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { api } from "@/api/api";

export default {
  name: "VisitorChatView",
  data() {
    return {
      destination: "",
      inputText: "",
      photo: "",
      statusText: "",
      errorMessage: "",
      isStarted: false,
      isTyping: false,
      messages: [],
      suggestions: [
        "推薦必訪景點",
        "規劃三日行程",
        "有哪些當地美食？",
        "交通方式與注意事項",
        "當地天氣與穿著建議",
      ],
    };
  },
  mounted() {
    const saved = localStorage.getItem("conversify-destination");
    if (saved) {
      this.destination = saved;
      this.isStarted = true;
    }
  },
  methods: {
    handleStart() {
      if (!this.destination.trim()) {
        this.errorMessage = "請先輸入即將前往的國家與區域";
        return;
      }
      this.errorMessage = "";
      localStorage.setItem("conversify-destination", this.destination);
      this.isStarted = true;
    },

    handleChangeDestination() {
      localStorage.removeItem("conversify-destination");
      this.destination = "";
      this.isStarted = false;
      this.messages = [];
      this.removePhoto();
    },

    // 移除待發送的照片
    removePhoto() {
      this.photo = "";
      this.statusText = "";
    },

    // 送出訊息
    async sendMessage(text) {
      const sendContent = (text || this.inputText).trim();

      if (!sendContent && !this.photo) return;

      const currentPhoto = this.photo; // 暫存目前照片 Base64

      this.messages.push({
        role: "user",
        content: sendContent || "請協助辨識剛拍攝的旅遊照片。",
        imageUrl: currentPhoto,
      });

      this.inputText = "";
      this.photo = "";
      this.statusText = "";
      this.isTyping = true;
      this.scrollToBottom();

      const promptText = `這是要前往${this.destination}的旅客，請以旅遊小助手的角色，提供有關${this.destination}的旅遊建議與資訊。`;
      const payloadMessages = JSON.parse(JSON.stringify(this.messages));
      Array.prototype.unshift.call(payloadMessages, {
        role: "system",
        content: promptText,
      });

      await api.chat
        .sendMessageByVisitor(payloadMessages)
        .then((response) => {
          const responseData = response.data;
          const aiReply = responseData.data.content;

          this.isTyping = false;
          this.messages.push({
            role: "assistant",
            content: aiReply,
          });
          this.scrollToBottom();
        })
        .catch((error) => {
          console.error("Error sending message:", error);
          this.isTyping = false;
          this.messages.push({
            role: "assistant",
            content: "抱歉，無法取得回覆，請稍後再試。",
          });
          this.scrollToBottom();
        });
    },

    handleEnterKey(e) {
      if (!e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },

    triggerCamera() {
      if (this.$refs.cameraInput) {
        this.$refs.cameraInput.value = null; // 清空舊值以利連續拍照
        this.$refs.cameraInput.click();
      }
    },

    handleCameraChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/") || file.size > 8 * 1024 * 1024) {
        this.statusText = "照片需為 8MB 以下的圖片檔。";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.photo = reader.result;
        this.statusText = "拍照完成，可輸入問題後送出。";
      };
      reader.readAsDataURL(file);
    },

    handleSpeechRecognition() {
      const Recognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Recognition) {
        this.statusText = "此瀏覽器不支援語音輸入。";
        return;
      }

      const recognition = new Recognition();
      recognition.lang = "zh-TW";
      this.statusText = "聆聽中…";

      recognition.onresult = (e) => {
        this.inputText = e.results[0][0].transcript;
      };
      recognition.onend = () => {
        this.statusText = "語音已填入，確認後即可送出。";
      };
      recognition.start();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatEl = this.$refs.appBox;
        if (chatEl) {
          console.log(
            "Scrolling to bottom of chat...",
            chatEl.scrollTop,
            chatEl.scrollHeight,
          );
          chatEl.scrollTop = chatEl.scrollHeight;
        }
      });
    },
  },
};
</script>

<style scoped>
:root {
  --yellow: #f3b51b;
  --ink: #292821;
  --muted: #77746b;
  --line: #ebe8df;
  --soft: #fff8e5;
  --shadow: 0 12px 34px rgba(48, 42, 26, 0.09);
}

* {
  box-sizing: border-box;
}

button,
input,
textarea {
  font: inherit;
}

button {
  cursor: pointer;
}

button:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 3px solid #f3b51b66;
  outline-offset: 2px;
}

.app {
  width: 100%;
  height: 100vh;
  background: #fff;
  overflow-x: hidden;
  color: #292821;
  font-family: system-ui, "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}

.header {
  height: 60px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  background: #fffffff5;
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(12px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-mark {
  width: 34px;
  height: 34px;
  border: 2px solid #f3b51b;
  border-radius: 10px;
  background: #fff6d5;
  display: grid;
  place-items: center;
  color: #9a6800;
  font-size: 12px;
  font-weight: 900;
}

.brand strong {
  display: block;
  font-family: Georgia, serif;
  font-size: 18px;
}

.brand small {
  display: block;
  color: #88847b;
  font-size: 9px;
}

.maker {
  font-size: 9px;
  font-weight: 700;
}

.content {
  min-height: calc(100vh - 60px);
}

.hero {
  height: clamp(176px, 48vw, 270px);
  position: relative;
  overflow: hidden;
  background: #fff4c9;
  border-radius: 0 0 22px 22px;
}

.hero > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hero-caption {
  display: none;
}

.chat-panel {
  position: relative;
  min-height: calc(100vh - 236px);
}

.chat {
  padding: 14px 10px 142px;
}

.message-row {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 10px;
}

.message-row.user {
  justify-content: flex-end;
}

.avatar {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #f3c64c;
  background: #fff;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 20%;
  transform: scale(1.65);
}

.bubble {
  max-width: calc(100% - 39px);
  padding: 9px 11px;
  border: 1px solid #eeece6;
  border-radius: 6px 16px 16px;
  background: #f7f7f5;
  font-size: 12.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* 對話對話框內的圖片 */
.image-preview {
  margin-bottom: 6px;
}

.image-preview img {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.user .bubble {
  background: #fff0b8;
  border-color: #f3d36b;
  border-radius: 16px 6px 16px 16px;
}

.destination-card {
  margin: 12px 0 0 39px;
  padding: 13px;
  border: 1px solid #ebe8df;
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(48, 42, 26, 0.09);
}

.destination-card label {
  display: block;
  font-weight: 750;
  font-size: 13px;
  margin-bottom: 8px;
}

.destination-card em {
  color: #b84622;
  font-style: normal;
}

.destination-card input {
  width: 100%;
  height: 44px;
  border: 1.5px solid #d9d6cd;
  border-radius: 13px;
  padding: 0 11px;
  font-size: 14px;
  background: #fbfbfa;
}

.start-button {
  width: 100%;
  height: 42px;
  margin-top: 10px;
  border: 0;
  border-radius: 13px;
  background: #f3b51b;
  font-size: 13px;
  font-weight: 800;
  color: #3c2a00;
}

.error {
  color: #b63a24;
  font-size: 11px;
  margin: 5px 2px 0;
}

.destination-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 9px 39px;
  padding: 5px 9px;
  border-radius: 99px;
  background: #fff8e5;
  color: #795309;
  font-size: 11px;
  font-weight: 700;
}

.destination-tag button {
  border: 0;
  background: none;
  color: #805d12;
  font-size: 10px;
  text-decoration: underline;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 14px 39px;
}

.chips button {
  min-height: 32px;
  padding: 6px 9px;
  border: 1px solid #f3b51b;
  border-radius: 99px;
  background: #fffdf8;
  color: #946100;
  font-size: 11px;
}

.composer-wrap {
  position: fixed;
  z-index: 9;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 5px 7px calc(7px + env(safe-area-inset-bottom));
  background: #fffffff5;
  border-top: 1px solid #e9e6df;
  box-shadow: 0 -8px 25px #30291612;
  backdrop-filter: blur(12px);
}

.composer {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px 40px;
  gap: 3px;
  align-items: end;
}

.icon-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  display: grid;
  place-items: center;
  background: #fff;
}

.icon-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.camera {
  background: #fff5d2;
  color: #986600;
}

.mic {
  border: 1px solid #eae3cc;
  color: #8c6516;
}

.send {
  background: #f3b51b;
  color: #fff;
}

.composer textarea {
  min-width: 0;
  min-height: 40px;
  max-height: 88px;
  resize: none;
  border: 1px solid #dfdcd4;
  border-radius: 20px;
  background: #f8f8f6;
  padding: 10px 10px;
  font-size: 13px;
  line-height: 1.3;
}

.composer textarea::placeholder {
  font-size: 12px;
}

/* 狀態欄與相片預覽樣式 */
.status-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 16px;
  margin-bottom: 4px;
}

.photo-preview-box {
  position: relative;
  display: inline-block;
}

.photo-preview-box img {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #f3b51b;
}

.remove-photo-btn {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #b63a24;
  color: #fff;
  border: none;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.status-text {
  color: #936305;
  font-size: 10px;
}

.typing {
  display: inline-flex;
  gap: 3px;
}

.typing i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f3b51b;
  animation: b 1s infinite;
}

.typing i:nth-child(2) {
  animation-delay: 0.15s;
}

.typing i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes b {
  50% {
    transform: translateY(-4px);
  }
}

@media (min-width: 600px) and (max-width: 1023px) {
  .header {
    height: 68px;
    padding-inline: 24px;
  }
  .hero {
    height: 320px;
    border-radius: 0;
  }
  .hero-caption {
    display: flex;
    position: absolute;
    left: 26px;
    bottom: 22px;
    max-width: 340px;
    flex-direction: column;
    padding: 14px 16px;
    border-radius: 17px;
    background: #fffdf1e8;
    box-shadow: 0 12px 34px rgba(48, 42, 26, 0.09);
  }
  .hero-caption span {
    color: #f3b51b;
  }
  .hero-caption small {
    margin-top: 3px;
    color: #77746b;
  }
  .chat {
    max-width: 760px;
    margin: auto;
    padding: 22px 28px 155px;
  }
  .composer-wrap {
    padding-inline: max(28px, calc((100% - 760px) / 2));
  }
  .composer {
    grid-template-columns: 42px minmax(0, 1fr) 42px 42px;
    gap: 5px;
  }
  .icon-button {
    width: 42px;
    height: 42px;
  }
  .composer textarea {
    min-height: 42px;
    font-size: 14px;
  }
  .bubble {
    font-size: 14px;
  }
  .destination-card {
    max-width: 580px;
  }
}

@media (min-width: 1024px) {
  .app {
    max-width: none;
    margin: 0;
    box-shadow: none;
  }
  .header {
    height: 72px;
    padding: 10px 30px;
  }
  .brand strong {
    font-size: 22px;
  }
  .brand small {
    font-size: 10px;
  }
  .maker {
    font-size: 12px;
  }
  .content {
    display: block;
    min-height: calc(100vh - 72px);
  }
  .hero {
    height: clamp(330px, 42vw, 520px);
    border-radius: 0 0 28px 28px;
  }
  .hero-caption {
    display: flex;
    position: absolute;
    left: 42px;
    bottom: 34px;
    max-width: 430px;
    flex-direction: column;
    padding: 18px 21px;
    border-radius: 20px;
    background: #fffdf1e8;
    box-shadow: 0 12px 34px rgba(48, 42, 26, 0.09);
    font-size: 18px;
  }
  .hero-caption span {
    color: #f3b51b;
  }
  .hero-caption small {
    font-size: 13px;
    margin-top: 4px;
    color: #77746b;
  }
  .chat-panel {
    min-height: 420px;
    background: #fff;
  }
  .chat {
    max-width: 900px;
    margin: auto;
    padding: 30px 38px 165px;
  }
  .bubble {
    font-size: 14px;
    max-width: min(680px, calc(100% - 44px));
  }
  .destination-card {
    max-width: 650px;
    padding: 20px;
  }
  .composer-wrap {
    left: 0;
    width: 100%;
    padding: 8px max(30px, calc((100% - 900px) / 2)) 12px;
    background: #fff;
  }
  .composer {
    grid-template-columns: 44px minmax(0, 1fr) 44px 44px;
    gap: 7px;
  }
  .icon-button {
    width: 44px;
    height: 44px;
  }
  .composer textarea {
    min-height: 44px;
    font-size: 14px;
  }
  .chips {
    max-width: 700px;
  }
}

@media (max-width: 350px) {
  .header {
    padding-inline: 8px;
  }
  .maker {
    font-size: 8px;
  }
  .brand small {
    display: none;
  }
  .chat {
    padding-inline: 7px;
  }
  .destination-card,
  .chips,
  .destination-tag {
    margin-left: 36px;
  }
  .composer-wrap {
    padding-inline: 4px;
  }
  .composer {
    grid-template-columns: 38px minmax(0, 1fr) 38px 38px;
    gap: 2px;
  }
  .icon-button {
    width: 38px;
    height: 38px;
  }
  .composer textarea {
    min-height: 38px;
    padding: 9px 8px;
    font-size: 12px;
  }
  .composer textarea::placeholder {
    font-size: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    scroll-behavior: auto !important;
  }
}
</style>
